import React from 'react';

const CLOUDFLARE_ACCOUNT_HASH = 'lzEB4WEiwuaDooGpiwwqdQ';
const CLOUDFLARE_BASE_URL = `https://imagedelivery.net/${CLOUDFLARE_ACCOUNT_HASH}`;

export type ImageVariant = 'public' | 'thumbnail' | 'avatar';

export const imageMap = {
  'braided-ponytail-style.jpg': '8f6abb2f-8d6e-46bd-edff-791d6b0ac600',
  'classic-box-braids-salon.jpg': 'ca0bfe81-e04a-42f1-adb5-fdeae7fd7b00',
  'faux-locs-protective-style.png': 'a7d17048-3b79-4d89-8146-8f1fef130200',
  'gigi-braids-gallery-01-box-braids.jpeg':
    'e04d56a5-dafe-405f-ec14-4fd69158d900',
  'gigi-braids-gallery-02-goddess-braids-og.jpeg':
    'f7d6bfad-80f6-4362-6d85-5f2bfe2f6100',
  'gigi-braids-gallery-02-goddess-braids.jpg':
    '2b278555-44b4-42ac-44b3-f0600f477300',
  'gigi-braids-gallery-03-feed-in-braids.jpeg':
    'b5193a95-55ed-4989-d590-eb7d2bc56a00',
  'gigi-braids-gallery-04-cornrows.jpeg':
    '59c5ec34-5923-4e0d-ec4f-19519b77a300',
  'gigi-braids-gallery-05-twists.jpeg': 'a4680cd9-be0f-47d9-035e-a60a294b0800',
  'gigi-braids-gallery-06-colored-braids.jpeg':
    '2aba17c3-a04d-461c-04f3-8f9599e72800',
  'gigi-braids-gallery-07-festival-braids.jpeg':
    '416576d5-3904-4b64-8d43-e74146fc2200',
  'gigi-braids-gallery-08-updo-braids.jpeg':
    'eb02cd37-1888-4fad-741b-6ce506059d00',
  'gigi-braids-gallery-09-long-braids.jpeg':
    '165eb16c-6158-45da-2d52-c3864d1d0900',
  'gigi-braids-gallery-10-short-braids.jpeg':
    'b0a3a9db-c032-40d5-7212-9b17207d1500',
  'gigi-braids-gallery-11-thick-braids.jpeg':
    '7de6825c-99dd-46e2-29e5-b257e9b5c800',
  'gigi-braids-gallery-12-thin-braids.jpeg':
    'a49c1fab-e188-49c5-c785-2a3dff193c00',
  'gigi-elpha-hair-braiding-expert.jpg': '2e59c7be-29f9-4159-bc83-857ce4f20800',
  'gigi-elpha-professional-braiding.jpeg':
    'b7f516e4-c4b6-4822-43f0-d476c5b41500',
  'hair-treatment-conditioning.jpeg': '597542cd-0ad4-417b-8ca2-8ad565e3dd00',
  'half-and-half-braiding-style.jpg': 'bd7ae5dd-45f3-4139-e775-16f8ca408200',
  'logo.png': 'c03ebab7-1d6a-4a34-2517-21a08ebdd400',
  'professional-hair-braiding-service.jpg':
    'b61a61b7-a3f9-4b2e-c6bf-497899303400',
  'styled-cornrows-protective-style.jpg':
    '22db7e10-55be-46a9-684a-7aec82beae00',
  'stylish-braids-protective-styling.png':
    '67be4f48-9218-420e-2e43-a9156ad2c000',
} as const;

export type ImageKey = keyof typeof imageMap;

export function getImageUrl(
  imageKey: ImageKey,
  variant: ImageVariant = 'public'
): string {
  const imageId = imageMap[imageKey];
  return `${CLOUDFLARE_BASE_URL}/${imageId}/${variant}`;
}

export function getResponsiveSrcSet(imageKey: ImageKey): string {
  const publicUrl = getImageUrl(imageKey, 'public');
  const thumbnailUrl = getImageUrl(imageKey, 'thumbnail');

  // Assuming thumbnail is ~200px wide and public is original size
  return `${thumbnailUrl} 200w, ${publicUrl} 1000w`;
}

export const Images = {
  // Logo
  logo: getImageUrl('logo.png'),
  logoThumbnail: getImageUrl('logo.png', 'thumbnail'),

  // Hero slides - newly uploaded high-quality images
  heroSlide1:
    'https://imagedelivery.net/lzEB4WEiwuaDooGpiwwqdQ/6b956e73-174d-425d-9f5f-0c38f3b3bc00/public',
  heroSlide2:
    'https://imagedelivery.net/lzEB4WEiwuaDooGpiwwqdQ/24e3ef97-94dc-4dfa-de16-69d85d52c600/public',
  heroSlide3:
    'https://imagedelivery.net/lzEB4WEiwuaDooGpiwwqdQ/5d9a1308-cb54-49c5-db32-257d7df03c00/public',
  heroSlide4:
    'https://imagedelivery.net/lzEB4WEiwuaDooGpiwwqdQ/5065aeb3-98a6-4eaa-0ff5-f85daed84f00/public',
  heroSlide5:
    'https://imagedelivery.net/lzEB4WEiwuaDooGpiwwqdQ/4b367075-1f8b-4cf8-03dc-dbf054167900/public',
  heroSlide6:
    'https://imagedelivery.net/lzEB4WEiwuaDooGpiwwqdQ/92cfe947-0d2a-4c6a-f6a8-0ccd727ed600/public',

  // About page
  gigiExpert: getImageUrl('gigi-elpha-hair-braiding-expert.jpg'),
  hairMaintenance: getImageUrl('hair-treatment-conditioning.jpeg'),
  artisticBraids: getImageUrl('styled-cornrows-protective-style.jpg'),
  boxBraids: getImageUrl('gigi-braids-gallery-01-box-braids.jpeg'),

  // Services
  serviceCornrows: getImageUrl('gigi-braids-gallery-10-short-braids.jpeg'),
  serviceBoxBraids: getImageUrl(
    'gigi-braids-gallery-02-goddess-braids-og.jpeg'
  ),
  serviceTwists: getImageUrl('gigi-braids-gallery-01-box-braids.jpeg'),
  serviceGoddess: getImageUrl('gigi-braids-gallery-02-goddess-braids.jpg'),
  serviceFeedIn: getImageUrl('gigi-braids-gallery-12-thin-braids.jpeg'),
  serviceHairMaintenance: getImageUrl('hair-treatment-conditioning.jpeg'),

  // Gallery images
  gallery: {
    classicBoxBraids: getImageUrl('classic-box-braids-salon.jpg'),
    goddessBraids: getImageUrl('gigi-braids-gallery-02-goddess-braids.jpg'),
    cornrowDesign: getImageUrl('stylish-braids-protective-styling.png'),
    twoStrandTwists: getImageUrl('faux-locs-protective-style.png'),
    feedInBraids: getImageUrl('half-and-half-braiding-style.jpg'),
    coloredBraids: getImageUrl('gigi-braids-gallery-06-colored-braids.jpeg'),
    protectiveStyle: getImageUrl('gigi-braids-gallery-01-box-braids.jpeg'),
    festivalBraids: getImageUrl('gigi-braids-gallery-07-festival-braids.jpeg'),
    bridalBraids: getImageUrl('gigi-braids-gallery-04-cornrows.jpeg'),
  },
} as const;

/**
 * Helper hook for lazy loading images with intersection observer
 * For optimal performance with Cloudflare Images
 */
export function useProgressiveImage(src: string): string {
  const [imgSrc, setImgSrc] = React.useState<string>('');

  React.useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setImgSrc(src);
    };
  }, [src]);

  return imgSrc;
}
